import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { BoardRows } from '@/components/predictions/BoardRows';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { isSupabaseConfigured } from '@/config';
import { useThemePalette } from '@/config/theme';
import { useMyPredictionStats, usePredictionLeaderboard } from '@/features/predictions/game';
import { useAuth } from '@/lib/auth/AuthProvider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

function StatCell({ value, label, divider }: { value: string; label: string; divider?: boolean }) {
  return (
    <View className={`flex-1 items-center py-3 ${divider ? 'border-s border-line' : ''}`}>
      <AppText variant="heading" weight="bold" tabular className="text-volt-bright">
        {value}
      </AppText>
      <AppText variant="micro" weight="medium" className="uppercase tracking-wide text-muted">
        {label}
      </AppText>
    </View>
  );
}

/**
 * Predictions hub: your standing + how-to-play + the global board. Picks now
 * live inside each match screen (MatchPickWidget); this tab is the social
 * "where do I rank" view, with a shortcut to private leagues.
 */
export default function PredictionsScreen() {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { userId } = useAuth();
  const { numeralOptions } = useLocale();
  const statsQuery = useMyPredictionStats();
  const boardQuery = usePredictionLeaderboard();
  const fmt = (value: number) => formatNumber(value, numeralOptions);

  return (
    <Screen>
      <View className="flex-row items-center justify-between px-4 pb-3 pt-2">
        <View className="flex-1">
          <AppText variant="title">{t('predictions.title')}</AppText>
          <AppText variant="micro" className="mt-0.5">
            {t('predictions.subtitle')}
          </AppText>
        </View>
        <Pressable
          accessibilityRole="button"
          onPress={() => router.push('/predictions/leaderboard')}
          className="flex-row items-center gap-2 rounded-full border border-volt-line bg-volt/10 px-3 py-2 active:bg-volt/20"
        >
          <Ionicons name="people" size={13} color={palette.voltBright} />
          <AppText variant="micro" weight="bold" className="text-volt-bright">
            {t('predictions.groups')}
          </AppText>
        </Pressable>
      </View>

      {!isSupabaseConfigured ? (
        <EmptyState
          icon="settings-outline"
          title={t('common.notConfiguredTitle')}
          body={t('common.notConfiguredBody')}
        />
      ) : (
        <ScrollView contentContainerStyle={{ paddingBottom: 24 }}>
          {userId ? (
            statsQuery.data ? (
              <Card className="mx-4 mb-4 flex-row overflow-hidden">
                <StatCell value={fmt(statsQuery.data.total_points)} label={t('profile.statPoints')} />
                <StatCell
                  value={fmt(statsQuery.data.correct)}
                  label={t('profile.statCorrect')}
                  divider
                />
                <StatCell value={`#${fmt(statsQuery.data.rank)}`} label={t('profile.statRank')} divider />
              </Card>
            ) : null
          ) : (
            <View className="mx-4 mb-4 rounded-card border border-line bg-surface px-3.5 py-2.5">
              <AppText variant="micro" className="text-soft">
                {t('predictions.signInToPlay')}
              </AppText>
            </View>
          )}

          <View className="mx-4 mb-4 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt/10 px-3.5 py-3">
            <Ionicons name="bulb-outline" size={16} color={palette.voltBright} />
            <AppText variant="micro" className="flex-1 leading-5 text-soft">
              {t('predictions.predictFromMatches')}
            </AppText>
          </View>

          <AppText
            variant="micro"
            weight="bold"
            className="mb-2 px-4 uppercase tracking-widest text-muted"
          >
            {t('predictions.topPredictors')}
          </AppText>
          {boardQuery.isPending ? (
            <View className="gap-3 px-4">
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} className="h-14 w-full" />
              ))}
            </View>
          ) : boardQuery.isError ? (
            <ErrorState error={boardQuery.error} onRetry={() => boardQuery.refetch()} />
          ) : (boardQuery.data ?? []).length === 0 ? (
            <EmptyState
              icon="podium-outline"
              title={t('predictions.emptyBoardTitle')}
              body={t('predictions.emptyBoardBody')}
            />
          ) : (
            <BoardRows entries={boardQuery.data ?? []} myUserId={userId} />
          )}
        </ScrollView>
      )}
    </Screen>
  );
}
